import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { AutoComplete, Spin, Empty } from 'antd';
import http from '../api/http';
import axios from '../utils/http';
import { useAuth } from '../hooks/useAuth';
import { useToasts } from '../hooks/useToasts';
import AddEmployeeModal from '../components/AddEmployeeModal';
import EmployeeDetailModal from '../components/EmployeeDetailModal';
// 移除防抖依赖，直接即时刷新更符合多选筛选体验

// Reusable Icon Component
const Icon = ({ path, className = 'w-5 h-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path d={path} />
  </svg>
);

const SearchIcon = () => <Icon path="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" />;

const BuildingIcon = ({ color = 'currentColor', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill={color}
      d="M5 2h10a1 1 0 011 1v14h-4v-4H8v4H4V3a1 1 0 011-1zm3 12h4v-3H8v3zm-2-5h3V6H6v3zm5 0h3V6h-3v3zm4 5v-3h-2v3h2zm-11 0h2v-3H4v3z"
    />
  </svg>
);

const SectionIcon = ({ color = 'currentColor', size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill={color}
      d="M3 4h14v2H3V4zm0 4h10v2H3V8zm0 4h7v2H3v-2z"
    />
  </svg>
);

const PersonIcon = ({ color = 'currentColor', size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fill={color}
      d="M10 10a3 3 0 100-6 3 3 0 000 6zm-6 6a6 6 0 1112 0v1H4v-1z"
    />
  </svg>
);
const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [columns, setColumns] = useState([]);
  // 唯一值与筛选
  const [distinctValues, setDistinctValues] = useState({ departments: [], sections: [], positions: [] });
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false); // 部门下拉开关
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('id');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  // 移除状态筛选：不再使用 statusFilter
  const [category, setCategory] = useState('main');
  // 导入预检相关状态
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  // 选择行用于批量转移
  const [selectedIds, setSelectedIds] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewCategory, setPreviewCategory] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const fileInputRef = useRef(null);
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedEmployeeCategory, setSelectedEmployeeCategory] = useState('main');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [useCardView, setUseCardView] = useState(true);
  const [internEmployees, setInternEmployees] = useState([]);

  const { hasPermission } = useAuth();
  const { showSuccess, showError } = useToasts();

  // 新增后高亮 & 滚动
  const [newEmployeeId, setNewEmployeeId] = useState(null);
  const rowRefs = useRef({});

  // 右键菜单 & 人事变动
  const [contextMenu, setContextMenu] = useState({ open: false, x: 0, y: 0, employee: null });
  const contextMenuRef = useRef(null);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [changeEmployee, setChangeEmployee] = useState(null);

  useEffect(() => {
    if (newEmployeeId && rowRefs.current[newEmployeeId]) {
      const el = rowRefs.current[newEmployeeId];
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => setNewEmployeeId(null), 3200);
      return () => clearTimeout(timer);
    }
  }, [newEmployeeId]);

  // 右键菜单外部点击与 ESC 关闭逻辑（避免之前使用 contextmenu 事件导致的潜在竞态）
  useEffect(() => {
    if (!contextMenu.open) return;
    const handleMouseDown = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(prev => ({ ...prev, open: false }));
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setContextMenu(prev => ({ ...prev, open: false }));
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [contextMenu.open]);

  const canAddEmployees = useMemo(() => hasPermission('add_employee'), [hasPermission]);
  // 使用后端已有权限 key: view_employees 作为查看详情判定
  const canViewEmployeeDetails = useMemo(() => hasPermission('view_employees'), [hasPermission]);
  // 可编辑判定（传给详情弹窗内部决定是否显示修改按钮）
  const canEditEmployee = useMemo(() => hasPermission('edit_employee'), [hasPermission]);

  const fetchColumns = useCallback(async () => {
    try {
      const response = await http.get('/employees/columns', { params:{ category } });
      setColumns(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('获取列定义失败:', err);
      showError('无法加载表格列定义。');
    }
  }, [showError, category]);

  const fetchDistinct = useCallback(async () => {
    try {
      const res = await http.get('/employees/distinct', { params: { category } });
      const data = res?.data;
      const safe = (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
      setDistinctValues({
        departments: Array.isArray(safe.departments) ? safe.departments : [],
        sections: Array.isArray(safe.sections) ? safe.sections : [],
        positions: Array.isArray(safe.positions) ? safe.positions : []
      });
    } catch (err) {
      console.error('获取唯一值失败', err);
    }
  }, [category]);

  const fetchEmployees = useCallback(async (keyword) => {
    setLoading(true);
    try {
      const params = { sortColumn, sortDirection, category };
      const effectiveKeyword = typeof keyword === 'string' ? keyword : searchKeywordRef.current;
      if (effectiveKeyword && effectiveKeyword.trim()) {
        params.name = effectiveKeyword.trim();
      }
      const response = await http.get('/employees', { params });
      const mainList = Array.isArray(response.data)
        ? response.data.map(emp => ({ ...emp, record_source: category || 'main' }))
        : [];
      setEmployees(mainList);
      setError(null);

      if (category === 'main') {
        try {
          const internParams = { category: 'intern' };
          if (effectiveKeyword && effectiveKeyword.trim()) {
            internParams.name = effectiveKeyword.trim();
          }
          const internRes = await http.get('/employees', { params: internParams });
          const internList = Array.isArray(internRes.data)
            ? internRes.data.map(emp => ({ ...emp, record_source: 'intern', status: emp.status || '实习人员' }))
            : [];
          setInternEmployees(internList);
        } catch (internErr) {
          console.error('获取实习人员失败:', internErr);
          setInternEmployees([]);
        }
      } else {
        setInternEmployees([]);
      }
    } catch (err) {
      console.error('获取员工列表失败:', err);
      setError('无法加载员工数据。请检查网络连接或稍后重试。');
      showError('获取员工列表失败。');
    } finally {
      setLoading(false);
    }
  }, [sortColumn, sortDirection, category, showError]);

  useEffect(() => { fetchColumns(); fetchDistinct(); }, [fetchColumns, fetchDistinct]);

  // 如果后端 distinct 返回空而前端已有员工数据，则用前端列表派生部门（客户端回退）
  useEffect(() => {
    const deptLen = (distinctValues && Array.isArray(distinctValues.departments)) ? distinctValues.departments.length : 0;
    if (deptLen === 0 && employees.length > 0) {
      const derived = Array.from(new Set(employees.map(e => e.department).filter(Boolean)));
      if (derived.length) {
        setDistinctValues(prev => ({ ...prev, departments: derived }));
      }
    }
  }, [
    (distinctValues && Array.isArray(distinctValues.departments)) ? distinctValues.departments.length : 0,
    employees
  ]);

  useEffect(() => {
    if (category !== 'main' || !internEmployees.length) return;
    setDistinctValues(prev => {
      const deptSet = new Set(prev.departments || []);
      const secSet = new Set(prev.sections || []);
      const posSet = new Set(prev.positions || []);
      let changed = false;
      internEmployees.forEach(emp => {
        if (emp.department && !deptSet.has(emp.department)) {
          deptSet.add(emp.department);
          changed = true;
        }
        if (emp.section && !secSet.has(emp.section)) {
          secSet.add(emp.section);
          changed = true;
        }
        if (emp.position && !posSet.has(emp.position)) {
          posSet.add(emp.position);
          changed = true;
        }
      });
      if (!changed) return prev;
      return {
        ...prev,
        departments: Array.from(deptSet),
        sections: Array.from(secSet),
        positions: Array.from(posSet)
      };
    });
  }, [internEmployees, category]);

  // 分类变化刷新，同时重置选中
  useEffect(() => { 
    setSelectedDepartments([]); setSelectedSections([]); setSelectedPositions([]); 
    // 将当前category保存到localStorage，供EmployeeDetailModal组件使用
    localStorage.setItem('currentEmployeeCategory', category);
    setSelectedEmployeeCategory(category);
    fetchEmployees(); 
  }, [category, fetchEmployees]);

  useEffect(() => {
    searchKeywordRef.current = searchTerm;
  }, [searchTerm]);

  useEffect(() => {
    if (isComposingRef.current) return;
    if (!searchTerm || !searchTerm.trim()) return;
    const timer = setTimeout(() => {
      fetchEmployees(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchEmployees]);

  useEffect(() => () => {
    suggestionRequestIdRef.current += 1;
  }, []);

  useEffect(() => {
    if (!useCardView) return;
    setSelectedIds(prev => (prev.length ? [] : prev));
    if (contextMenu.open) {
      setContextMenu(prev => ({ ...prev, open: false }));
    }
  }, [useCardView, contextMenu.open]);

  // 纯前端过滤：根据已选的部门/科室/岗位过滤 employees
  const displayedEmployees = useMemo(() => {
    return employees.filter(emp => {
      if (selectedDepartments.length && emp.department && !selectedDepartments.includes(emp.department)) return false;
      if (selectedSections.length && emp.section && !selectedSections.includes(emp.section)) return false;
      if (selectedPositions.length && emp.position && !selectedPositions.includes(emp.position)) return false;
      return true;
    });
  }, [employees, selectedDepartments, selectedSections, selectedPositions]);

  const displayedInterns = useMemo(() => {
    if (category !== 'main') return [];
    return internEmployees.filter(emp => {
      if (selectedDepartments.length && emp.department && !selectedDepartments.includes(emp.department)) return false;
      if (selectedSections.length && emp.section && !selectedSections.includes(emp.section)) return false;
      if (selectedPositions.length && emp.position && !selectedPositions.includes(emp.position)) return false;
      return true;
    });
  }, [internEmployees, selectedDepartments, selectedSections, selectedPositions, category]);

  const compareNames = useCallback((a = '', b = '') => {
    return (a || '').localeCompare(b || '', 'zh-Hans-CN', { sensitivity: 'base', numeric: true });
  }, []);

  const groupingSource = useMemo(() => {
    if (!useCardView) return displayedEmployees;
    if (category === 'main') {
      return [...displayedEmployees, ...displayedInterns];
    }
    return displayedEmployees;
  }, [useCardView, displayedEmployees, displayedInterns, category]);

  const groupedEmployees = useMemo(() => {
    if (!useCardView) return [];
    const deptMap = new Map();
    groupingSource.forEach(emp => {
      const isIntern = emp?.record_source === 'intern';
      const deptName = isIntern
        ? '实习人员'
        : (emp?.department && String(emp.department).trim() ? String(emp.department).trim() : '未分配部门');

      const originalDepartment = emp?.department && String(emp.department).trim() ? String(emp.department).trim() : '未分配部门';
      const sectionName = isIntern
        ? (emp?.section && String(emp.section).trim()
            ? String(emp.section).trim()
            : originalDepartment !== '未分配部门'
              ? originalDepartment
              : '未分配科室')
        : (emp?.section && String(emp.section).trim() ? String(emp.section).trim() : '未分配科室');

      let teamLabel;
      if (isIntern) {
        const teamParts = [];
        if (emp?.team && String(emp.team).trim()) {
          teamParts.push(String(emp.team).trim());
        }
        if (emp?.position && String(emp.position).trim()) {
          teamParts.push(String(emp.position).trim());
        }
        if (!teamParts.length && emp?.department && String(emp.department).trim()) {
          teamParts.push(String(emp.department).trim());
        }
        const uniqueTeamParts = Array.from(new Set(teamParts.filter(Boolean)));
        teamLabel = uniqueTeamParts.length ? uniqueTeamParts.join(' · ') : '未分配班组';
      } else {
        teamLabel = (emp?.team && String(emp.team).trim()) ? String(emp.team).trim() : '未分配班组';
      }

      if (!deptMap.has(deptName)) {
        deptMap.set(deptName, { total: 0, sections: new Map() });
      }
      const dept = deptMap.get(deptName);
      dept.total += 1;

      if (!dept.sections.has(sectionName)) {
        dept.sections.set(sectionName, { total: 0, teams: new Map() });
      }
      const section = dept.sections.get(sectionName);
      section.total += 1;

      if (!section.teams.has(teamLabel)) {
        section.teams.set(teamLabel, []);
      }
      const teamBucket = section.teams.get(teamLabel);
      teamBucket.push(emp);
    });

    const departments = Array.from(deptMap.entries()).map(([deptName, value]) => {
      const sections = Array.from(value.sections.entries()).map(([sectionName, sectionValue]) => {
        const teams = Array.from(sectionValue.teams.entries()).map(([teamName, employeesInTeam]) => ({
          name: teamName,
          employees: employeesInTeam
            .slice()
            .sort((a, b) => compareNames(a?.name || '', b?.name || ''))
        }));

        teams.sort((a, b) => {
          if (b.employees.length !== a.employees.length) {
            return b.employees.length - a.employees.length;
          }
          return compareNames(a.name, b.name);
        });

        return {
          name: sectionName,
          total: sectionValue.total,
          teams
        };
      });

      sections.sort((a, b) => {
        if ((b.total || 0) !== (a.total || 0)) {
          return (b.total || 0) - (a.total || 0);
        }
        return compareNames(a.name, b.name);
      });

      return {
        name: deptName,
        total: value.total,
        sections
      };
    });

    departments.sort((a, b) => {
      if ((b.total || 0) !== (a.total || 0)) {
        return (b.total || 0) - (a.total || 0);
      }
      return compareNames(a.name, b.name);
    });

    return departments;
  }, [useCardView, groupingSource, compareNames]);

  const renderedGroups = useMemo(() => {
    if (!useCardView) return [];
    const palette = ['#2563eb', '#f97316', '#0ea5e9', '#8b5cf6', '#22c55e', '#ec4899', '#14b8a6', '#d946ef'];
    const specialSections = new Set(['原料', '闪速炉', '转炉', '阳极炉']);
    const groups = [];

    groupedEmployees.forEach(dept => {
      if (dept.name === '熔炼厂') {
        const remainingSections = [];
        dept.sections.forEach(section => {
          if (specialSections.has(section.name)) {
            groups.push({
              name: section.name,
              total: section.total || 0,
              sections: [{ ...section }],
              parentDepartment: dept.name
            });
          } else {
            remainingSections.push(section);
          }
        });
        if (remainingSections.length) {
          const remainingTotal = remainingSections.reduce((sum, section) => sum + (section.total || 0), 0);
          groups.push({
            name: dept.name,
            total: remainingTotal,
            sections: remainingSections,
            parentDepartment: dept.name
          });
        }
      } else {
        groups.push({ ...dept, parentDepartment: dept.name });
      }
    });

    const filtered = groups.filter(group => group.total > 0);
    const isInternGroup = (group) => {
      const label = group.name || '';
      const parent = group.parentDepartment || '';
      return label === '实习人员' || parent === '实习人员';
    };

    filtered.sort((a, b) => {
      const aIntern = isInternGroup(a);
      const bIntern = isInternGroup(b);
      if (aIntern && !bIntern) return -1;
      if (!aIntern && bIntern) return 1;
      const aTotal = a.total || 0;
      const bTotal = b.total || 0;
      if (bTotal !== aTotal) return bTotal - aTotal;
      return compareNames(a.name, b.name);
    });

    return filtered.map((group, idx) => ({
      ...group,
      color: palette[idx % palette.length]
    }));
  }, [useCardView, groupedEmployees, compareNames]);

  // 通用日期格式化
  const formatDate = useCallback((value) => {
    if (!value) return value;
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const y = d.getFullYear();
      const m = String(d.getMonth()+1).padStart(2,'0');
      const day = String(d.getDate()).padStart(2,'0');
      return `${y}-${m}-${day}`;
    } catch { return value; }
  }, []);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleOpenDetailModal = (employee) => {
    if (!employee) return;
    const sourceCategory = employee.record_source || category;
    if (!canViewEmployeeDetails) {
      showError('当前账号缺少查看员工权限');
      return;
    }
    setSelectedEmployeeCategory(sourceCategory);
    setSelectedEmployeeId(employee.id);
    setIsDetailModalOpen(true);
  };

  const handleSearchSuggestions = useCallback(async (value) => {
    setSearchTerm(value);
    if (isComposingRef.current) {
      return;
    }
    suggestionRequestIdRef.current += 1;
    const currentRequest = suggestionRequestIdRef.current;
    if (!value || !value.trim()) {
      setSearchOptions([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const keyword = value.trim();
    try {
      const [mainRes, internRes] = await Promise.allSettled([
        http.get('/employees', { params: { name: keyword } }),
        http.get('/employees', { params: { name: keyword, category: 'intern' } })
      ]);

      if (suggestionRequestIdRef.current !== currentRequest) return;

      const mainList = mainRes.status === 'fulfilled' && Array.isArray(mainRes.value.data)
        ? mainRes.value.data.map(emp => ({ ...emp, record_source: emp.record_source || 'main' }))
        : [];
      const internList = internRes.status === 'fulfilled' && Array.isArray(internRes.value.data)
        ? internRes.value.data.map(emp => ({ ...emp, record_source: 'intern' }))
        : [];

      const combined = mainList.concat(internList);
      const opts = combined.slice(0, 12).map(emp => ({
        value: emp.name,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              {emp.record_source === 'intern' && <span style={{ color: '#2563eb', fontSize: '12px', marginRight: '6px' }}>[实习]</span>}
              {emp.name}
            </span>
            <span style={{ color: '#94a3b8', fontSize: '12px', marginLeft: '12px' }}>
              {[emp.department, emp.position || emp.section].filter(Boolean).join(' / ') || '未设置部门'}
            </span>
          </div>
        ),
        employeeId: emp.id,
        recordSource: emp.record_source || 'main'
      }));
      setSearchOptions(opts);
    } catch (err) {
      if (suggestionRequestIdRef.current === currentRequest) {
        console.error('员工检索失败', err);
      }
    } finally {
      if (suggestionRequestIdRef.current === currentRequest) {
        setSearchLoading(false);
      }
    }
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    if (!value || !value.trim()) {
      setSearchOptions([]);
      fetchEmployees('');
    }
  }, [fetchEmployees]);

  const handleSearchSelect = useCallback((value) => {
    setSearchTerm(value);
    setSearchOptions([]);
    fetchEmployees(value);
  }, [fetchEmployees]);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback((e) => {
    isComposingRef.current = false;
    const nextValue = e?.target?.value ?? '';
    handleSearchSuggestions(nextValue);
  }, [handleSearchSuggestions]);

  // 删除功能已移除

  const containerStyle = {
    padding: '2rem',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    height: '100%',
    fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  };

  const searchContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  };

  const searchInputStyle = {
    padding: '0.75rem 1rem',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'box-shadow 0.2s, border-color 0.2s',
  };

  const primaryButtonStyle = {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'var(--brand-primary)',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 600,
    transition: 'background-color 0.2s',
  };

  const tableContainerStyle = {
    overflowX: 'auto',
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    border: '1px solid var(--border-color)',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
    fontSize: '14px',
  };

  const thStyle = {
    padding: '16px 20px',
    backgroundColor: 'var(--bg-tertiary)',
    borderBottom: '1px solid var(--border-color)',
    cursor: 'pointer',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    letterSpacing: '0.5px',
  };

  const tdStyle = {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    verticalAlign: 'middle',
  };

  const actionButtonStyle = {
    background: 'none',
    border: 'none',
    color: 'var(--brand-primary)',
    cursor: 'pointer',
    marginRight: '15px',
    fontWeight: 500,
    transition: 'color 0.2s',
  };

  const [hoveredRow, setHoveredRow] = useState(null);
  // 双击备用检测（防止某些环境 onDoubleClick 未触发）
  const dblFallbackRef = useRef({ id: null, time: 0 });
  const searchKeywordRef = useRef('');
  const suggestionRequestIdRef = useRef(0);
  const isComposingRef = useRef(false);

  // 序号列与筛选数量展示
  const totalCount = employees.length + (useCardView && category === 'main' ? internEmployees.length : 0);
  const filteredCount = useCardView && category === 'main'
    ? displayedEmployees.length + displayedInterns.length
    : displayedEmployees.length;

  if (loading || columns.length === 0) {
    return <div style={containerStyle}>正在加载员工列表...</div>;
  }

  if (error) {
    return <div style={containerStyle}>{error}</div>;
  }

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700 }}>人员列表</h1>
        <div style={searchContainerStyle}>
          <div style={{ fontSize:'0.75rem', padding:'0.4rem 0.8rem', background:'var(--bg-secondary)', border:'1px solid var(--border-color)', borderRadius:'8px', color:'var(--text-secondary)' }}>
            筛选结果：<strong style={{ color:'var(--brand-primary)' }}>{filteredCount}</strong> / {totalCount}
          </div>
          <div style={searchInputStyle} onFocusCapture={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'} onBlurCapture={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}>
            <SearchIcon />
            <AutoComplete
              value={searchTerm}
              options={searchOptions}
              onSearch={handleSearchSuggestions}
              onChange={handleSearchChange}
              onSelect={handleSearchSelect}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              allowClear
              placeholder="搜索姓名、部门..."
              style={{ width: 240, minWidth: 200 }}
              popupMatchSelectWidth={320}
              notFoundContent={searchLoading ? <Spin size="small" /> : null}
            />
          </div>
          {/* 状态筛选已移除 */}
          <div style={searchInputStyle}>
            <select value={category} onChange={e=> { setCategory(e.target.value); }} style={{ background:'transparent', border:'none', outline:'none', color:'inherit' }}>
              <option value="main">在职</option>
              <option value="temp">临时人员</option>
              <option value="resigned">离职人员</option>
              <option value="intern">实习人员</option>
            </select>
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:'0.35rem', padding:'0.55rem 0.8rem', border:'1px solid var(--border-color)', borderRadius:'8px', background:'var(--bg-secondary)', color:'var(--text-primary)', fontSize:'0.78rem', cursor:'pointer' }}>
            <input
              type="checkbox"
              checked={!useCardView}
              onChange={e => setUseCardView(!e.target.checked)}
              style={{ cursor:'pointer' }}
            />
            <span>列表显示</span>
          </label>
          {/* 部门下拉多选 */}
          <div style={{ position:'relative' }}>
            <button
              type="button"
              onClick={() => setShowDeptDropdown(prev => !prev)}
              style={{
                padding:'0.5rem 0.75rem',
                border:'1px solid var(--border-color)',
                background:'var(--bg-secondary)',
                color:'var(--text-primary)',
                borderRadius:'8px',
                fontSize:'12px',
                minWidth:'150px',
                display:'flex',
                alignItems:'center',
                justifyContent:'space-between',
                gap:'6px',
                cursor:'pointer'
              }}
            >
              <span>部门筛选{selectedDepartments.length>0?`(${selectedDepartments.length})`:''}</span>
              <span style={{ transform: showDeptDropdown? 'rotate(180deg)':'rotate(0)', transition:'transform 0.15s' }}>▾</span>
            </button>
            {showDeptDropdown && (
              <div style={{ position:'absolute', top:'110%', left:0, zIndex:1100, background:'var(--bg-secondary)', border:'1px solid var(--border-color)', borderRadius:'10px', padding:'8px 10px', boxShadow:'0 8px 18px -4px rgba(0,0,0,0.15)', width:'240px', maxHeight:'260px', overflow:'auto' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                  <strong style={{ fontSize:'12px' }}>选择部门</strong>
                  {selectedDepartments.length>0 && (
                    <button onClick={()=> { setSelectedDepartments([]); fetchEmployees(); }} style={{ background:'none', border:'none', color:'#ef4444', fontSize:'11px', cursor:'pointer' }}>清空</button>
                  )}
                </div>
                {distinctValues.departments.length===0 && (
                  <div style={{ fontSize:'11px', color:'var(--text-secondary)' }}>暂无数据</div>
                )}
                {distinctValues.departments.map(dep => (
                  <label key={dep} style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', padding:'4px 4px', borderRadius:'6px', cursor:'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedDepartments.includes(dep)}
                      onChange={(e)=>{
                        setSelectedDepartments(prev => {
                          const next = e.target.checked ? [...prev, dep] : prev.filter(d=> d!==dep);
                          // 立即刷新而不是等防抖
                          fetchEmployees();
                          return next;
                        });
                      }}
                    />
                    <span style={{ flex:1 }}>{dep}</span>
                  </label>
                ))}
                {distinctValues.departments.length>0 && (
                  <div style={{ marginTop:'8px', display:'flex', gap:'6px', flexWrap:'wrap' }}>
                    {selectedDepartments.map(dep => (
                      <span key={dep} style={{ background:'#1e293b', color:'#fff', fontSize:'11px', padding:'2px 6px', borderRadius:'999px', display:'flex', alignItems:'center', gap:'4px' }}>
                        {dep}
                        <button
                          onClick={() => setSelectedDepartments(prev => { const next = prev.filter(d=> d!==dep); fetchEmployees(); return next; })}
                          style={{ background:'none', border:'none', color:'#fff', fontSize:'11px', cursor:'pointer', lineHeight:1 }}
                          aria-label={`移除 ${dep}`}
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {canAddEmployees && (
            <button 
              style={primaryButtonStyle} 
              onClick={() => setIsAddModalOpen(true)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--brand-dark)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--brand-primary)'}
            >
              新增员工
            </button>
          )}
          <div style={{ display:'flex', gap:'0.75rem' }}>
            <button
              style={{...primaryButtonStyle, background:'#4b5563'}}
              onClick={()=>{ setShowImportModal(true); setPreviewData(null); setImportResult(null); setPreviewError(null); setPendingImportFile(null); setPreviewCategory(null); }}
              disabled={importLoading}
            >{importLoading ? '处理中...' : '导入员工'}</button>
            <input ref={fileInputRef} type="file" accept=".xls,.xlsx,.csv" style={{display:'none'}} onChange={async (e)=>{
              const file = e.target.files?.[0]; if(!file) return;
              setPendingImportFile(file); setPreviewError(null); setPreviewLoading(true); setPreviewData(null);
              try {
                const fd = new FormData(); fd.append('file', file);
                const res = await axios.post(`/employees/import/preview?category=${previewCategory}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
                setPreviewData(res.data);
              } catch(err){ setPreviewError(err?.message || '预检失败'); }
              finally { setPreviewLoading(false); }
              e.target.value='';
            }} />
            <button
              style={{...primaryButtonStyle, background:'#6366f1'}}
              disabled={selectedIds.length===0}
              onClick={()=>{
                if(selectedIds.length===0){ showError('请先勾选要转移的员工'); return; }
                const target = window.prompt('请输入目标分类: main/temp/resigned/intern', category==='main'?'resigned':'main');
                if(!target) return;
                if(!['main','temp','resigned','intern'].includes(target)){ showError('分类无效'); return; }
                axios.post('/employees/transfer', { ids: selectedIds, sourceCategory: category, targetCategory: target })
                  .then(res=>{ showSuccess(`转移完成: ${res.data.transferred || 0} 条, 跳过 ${res.data.skipped || 0}`); setSelectedIds([]); fetchEmployees(); })
                  .catch(err=> showError(err?.message || '转移失败'));
              }}
            >批量转移</button>
            <button
              style={{...primaryButtonStyle, background:'#0d9488'}}
              onClick={async ()=>{
                try {
                  const res = await axios.get(`/employees/export?category=${category}&format=csv`, { responseType:'blob' });
                  const url = window.URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url; a.download = `employees_${category}.csv`; a.click();
                  window.URL.revokeObjectURL(url);
                  showSuccess('CSV 导出完成');
                } catch(err){ showError(err?.message || '导出失败'); }
              }}
            >导出CSV</button>
            <button
              style={{...primaryButtonStyle, background:'#059669'}}
              onClick={async ()=>{
                try {
                  const res = await axios.get(`/employees/export?category=${category}&format=excel`, { responseType:'blob' });
                  const url = window.URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url; a.download = `employees_${category}.xlsx`; a.click();
                  window.URL.revokeObjectURL(url);
                  showSuccess('Excel 导出完成');
                } catch(err){ showError(err?.message || '导出失败'); }
              }}
            >导出Excel</button>
          </div>
        </div>
      </header>
      {importResult && (
        <div style={{background:'var(--bg-secondary)', border:'1px solid var(--border-color)', padding:'12px 16px', borderRadius:'10px', marginBottom:'16px'}}>
          <b>导入结果:</b>
          <pre style={{margin:0, maxHeight:180, overflow:'auto', fontSize:'12px', background:'var(--bg-tertiary)', padding:'8px', borderRadius:'6px'}}>{JSON.stringify(importResult,null,2)}</pre>
        </div>
      )}

      {useCardView ? (
        renderedGroups.length ? (
          <div style={{ marginTop:'1.25rem', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:'1.25rem' }}>
            {renderedGroups.map(dept => (
              <div key={`${dept.parentDepartment || '未分配'}-${dept.name}`} style={{ background:'var(--bg-secondary)', border:'1px solid var(--border-color)', borderRadius:'14px', padding:'1rem 1.1rem', display:'flex', flexDirection:'column', gap:'0.9rem', boxShadow:'0 16px 30px -18px rgba(15,23,42,0.4)' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.65rem' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:38, height:38, borderRadius:'999px', background:`${dept.color}1a`, color:dept.color }}>
                      <BuildingIcon color={dept.color} size={22} />
                    </span>
                    <div>
                      <div style={{ fontSize:'1rem', fontWeight:600 }}>
                        {dept.parentDepartment && dept.parentDepartment !== dept.name
                          ? `${dept.parentDepartment} · ${dept.name}`
                          : dept.name}
                      </div>
                      <div style={{ fontSize:'0.76rem', color:'var(--text-secondary)' }}>{dept.total} 人</div>
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                  {dept.sections.map(section => (
                    <div key={`${dept.name || '未分配'}-${section.name}`} style={{ border:'1px dashed rgba(148,163,184,0.45)', borderRadius:'10px', padding:'0.65rem 0.75rem', background:'var(--bg-primary)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.55rem' }}>
                        <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28, borderRadius:'50%', background:'rgba(148,163,184,0.2)', color:dept.color }}>
                          <SectionIcon color={dept.color} size={16} />
                        </span>
                        <span style={{ fontWeight:500, fontSize:'0.9rem' }}>{section.name}</span>
                        <span style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>{section.total || 0} 人</span>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
                        {(section.teams || []).map(team => (
                          <div key={`${dept.name || '未分配'}-${section.name}-${team.name}`} style={{ border:'1px solid rgba(148,163,184,0.25)', borderRadius:'8px', padding:'0.45rem 0.55rem', background:'rgba(15,23,42,0.04)' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.4rem' }}>
                              <span style={{ fontSize:'0.8rem', fontWeight:500, color:dept.color }}>{team.name}</span>
                              <span style={{ fontSize:'0.72rem', color:'var(--text-secondary)' }}>{team.employees.length} 人</span>
                            </div>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:'0.45rem' }}>
                              {team.employees.map((emp, idx) => {
                                const key = `${emp?.record_source || 'main'}-${emp?.id ?? `${dept.name || '未分配'}-${section.name}-${team.name}-${idx}`}`;
                                const isInternEntry = emp?.record_source === 'intern';
                                return (
                                  <button
                                    key={key}
                                    onClick={() => { if (emp?.id) handleOpenDetailModal(emp); }}
                                    style={{ display:'inline-flex', alignItems:'center', gap:'0.35rem', padding:'0.3rem 0.7rem', borderRadius:'999px', border:'1px solid rgba(148,163,184,0.4)', background:'var(--bg-secondary)', color:'var(--text-primary)', fontSize:'0.75rem', cursor: emp?.id ? 'pointer' : 'not-allowed', opacity: isInternEntry ? 0.88 : 1 }}
                                    title={emp?.name || '未命名'}
                                  >
                                    <PersonIcon color={dept.color} size={12} />
                                    <span>{emp?.name || '未命名'}</span>
                                    {isInternEntry && <span style={{ fontSize:'0.7rem', color:dept.color, marginLeft:'0.25rem' }}>实习</span>}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ marginTop:'1.5rem', padding:'2.5rem 1.5rem', background:'var(--bg-secondary)', border:'1px solid var(--border-color)', borderRadius:'12px' }}>
            <Empty description="暂无人员数据" />
          </div>
        )
      ) : (
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>序号</th>
                <th style={thStyle}>
                  <input type="checkbox" checked={selectedIds.length>0 && employees.length>0 && selectedIds.length===employees.length}
                    onChange={(e)=>{
                      if(e.target.checked){ setSelectedIds(employees.map(emp=>emp.id)); } else { setSelectedIds([]); }
                    }}
                  />
                </th>
                {columns.map(col => {
                  const active = sortColumn === col.key;
                  const arrow = active ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '';
                  return (
                    <th
                      key={col.key}
                      style={{ ...thStyle, color: active ? 'var(--brand-primary)' : thStyle.color }}
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}{arrow}
                    </th>
                  );
                })}
                <th style={thStyle}>操作</th>
              </tr>
            </thead>
            <tbody>
              {displayedEmployees.map((employee, index) => {
                const isNew = employee.id === newEmployeeId;
                return (
                <tr
                  key={employee.id}
                  ref={(el) => { if (el) rowRefs.current[employee.id] = el; }}
                  style={{
                    backgroundColor: isNew ? 'rgba(34,197,94,0.12)' : (index % 2 === 0 ? 'var(--bg-secondary)' : 'var(--bg-primary)'),
                    transition: 'background-color 0.25s, box-shadow 0.3s',
                    ...(hoveredRow === employee.id && { backgroundColor: 'var(--bg-tertiary)' }),
                    ...(isNew && { boxShadow: '0 0 0 2px rgba(34,197,94,0.45) inset' }),
                    cursor: 'pointer'
                  }}
                  onMouseEnter={() => setHoveredRow(employee.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  onDoubleClick={() => handleOpenDetailModal(employee)}
                  onClick={() => {
                    const now = Date.now();
                    const { id: lastId, time: lastTime } = dblFallbackRef.current;
                    if (lastId === employee.id && now - lastTime < 320) {
                      handleOpenDetailModal(employee);
                    }
                    dblFallbackRef.current = { id: employee.id, time: now };
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    // 视口边界调整，防止菜单溢出窗口
                    const viewportW = window.innerWidth;
                    const viewportH = window.innerHeight;
                    const menuWidth = 180; // 预估
                    const menuHeight = 90; // 预估
                    let posX = e.clientX;
                    let posY = e.clientY;
                    if (posX + menuWidth > viewportW) posX = viewportW - menuWidth - 4;
                    if (posY + menuHeight > viewportH) posY = viewportH - menuHeight - 4;
                    setContextMenu({ open: true, x: posX, y: posY, employee });
                  }}
                  title="双击查看详情，右键打开操作菜单"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleOpenDetailModal(employee);
                    if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setContextMenu({ open: true, x: rect.left + 12, y: rect.top + 12, employee });
                    }
                  }}
                >
                  <td style={tdStyle}>{index + 1}</td>
                  <td style={tdStyle}>
                    <input type="checkbox" checked={selectedIds.includes(employee.id)} onChange={(e)=>{
                      setSelectedIds(prev=> e.target.checked ? [...prev, employee.id] : prev.filter(id=> id!==employee.id));
                    }} />
                  </td>
                  {columns.map(col => {
                    let value = employee[col.key];
                    if (col.key === 'is_dual_employee') value = employee[col.key] ? '是' : '否';
                    // 统一日期字段格式化（扩展）
                    if (['birth_date','hire_date','resigned_date','contract_end_date','on_the_job_medical_exam_date'].includes(col.key)) {
                      value = formatDate(value);
                    }
                    return <td key={col.key} style={tdStyle}>{value}</td>;
                  })}
                  <td style={tdStyle}>
                    {canViewEmployeeDetails && (
                      <button 
                        style={actionButtonStyle} 
                        onClick={() => handleOpenDetailModal(employee)}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-dark)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--brand-primary)'}
                      >
                        详情
                      </button>
                    )}
                    {/* 删除按钮已移除 */}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {/* 右键菜单 */}
      {!useCardView && contextMenu.open && contextMenu.employee && (
        <div
          ref={contextMenuRef}
          role="menu"
          aria-label="行操作菜单"
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            background: '#fff',
            border: '1px solid rgba(148,163,184,0.35)',
            boxShadow: '0 6px 18px -4px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            zIndex: 1200,
            padding: '0.35rem 0',
            minWidth: '170px'
          }}
        >
          <button
            role="menuitem"
            style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '0.5rem 0.9rem', fontSize: '0.78rem', cursor: 'pointer' }}
            onClick={() => {
              setChangeEmployee(contextMenu.employee);
              setShowChangeModal(true);
              setContextMenu(prev => ({ ...prev, open: false }));
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setChangeEmployee(contextMenu.employee); setShowChangeModal(true); setContextMenu(prev => ({ ...prev, open: false })); } }}
          >
            人事变动
          </button>
          <button
            role="menuitem"
            style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '0.5rem 0.9rem', fontSize: '0.78rem', cursor: 'pointer' }}
            onClick={() => {
              handleOpenDetailModal(contextMenu.employee);
              setContextMenu(prev => ({ ...prev, open: false }));
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') { handleOpenDetailModal(contextMenu.employee); setContextMenu(prev => ({ ...prev, open: false })); } }}
          >
            查看详情
          </button>
        </div>
      )}

      {/* 人事变动弹窗占位 */}
      {showChangeModal && changeEmployee && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
          <div style={{ width: '520px', maxWidth: '90%', background: '#fff', borderRadius: '14px', padding: '1rem 1.2rem 1.25rem', boxShadow: '0 18px 40px -12px rgba(0,0,0,0.4)' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>人事变动 - {changeEmployee.name}</h3>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>这里可以填写员工岗位调整、部门调动、合同更新等信息（此处为占位，可根据后端字段扩展）。</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.75rem' }}>
              <textarea placeholder="变动说明..." style={{ width: '100%', minHeight: '110px', fontSize: '0.8rem', padding: '0.55rem 0.7rem', borderRadius: '8px', border: '1px solid rgba(148,163,184,0.4)', resize: 'vertical' }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '0.4rem' }}>
                <button onClick={() => { setShowChangeModal(false); setChangeEmployee(null); }} style={{ padding: '0.5rem 0.9rem', background: 'var(--bg-secondary)', border: '1px solid rgba(148,163,184,0.4)', borderRadius: '8px', fontSize: '0.75rem', cursor: 'pointer' }}>取消</button>
                <button onClick={() => { showSuccess('已保存（示例）'); setShowChangeModal(false); setChangeEmployee(null); }} style={{ padding: '0.5rem 0.9rem', background: 'var(--brand-primary)', color: '#fff', border: '1px solid var(--brand-primary)', borderRadius: '8px', fontSize: '0.75rem', cursor: 'pointer' }}>保存</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canAddEmployees && (
        <AddEmployeeModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={(newEmp) => {
            fetchEmployees().then(() => {
              if (newEmp && newEmp.id) setNewEmployeeId(newEmp.id);
            });
            setIsAddModalOpen(false);
          }}
        />
      )}

      {/* 导入员工弹窗：选择分类 -> 选择文件 -> 预检简表 -> 确认导入 */}
      {showImportModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1500 }}>
          <div style={{ width:'640px', maxWidth:'94%', background:'#ffffff', borderRadius:'16px', padding:'1.2rem 1.25rem 1.3rem', boxShadow:'0 30px 60px -15px rgba(0,0,0,0.5)', display:'flex', flexDirection:'column', gap:'0.9rem' }}>
            <h2 style={{ margin:0, fontSize:'1.15rem' }}>导入员工数据</h2>
            <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 160px', minWidth:'160px' }}>
                <label style={{ fontSize:'0.7rem', fontWeight:600, color:'#475569' }}>分类</label>
                <select value={previewCategory || ''} onChange={e=> setPreviewCategory(e.target.value)} style={{ width:'100%', marginTop:'4px', padding:'6px 8px', borderRadius:'8px', border:'1px solid #cbd5e1', background:'#f8fafc' }}>
                  <option value="">请选择分类</option>
                  <option value="main">在职</option>
                  <option value="resigned">离职人员</option>
                  <option value="temp">临时人员</option>
                  <option value="intern">实习人员</option>
                </select>
              </div>
              <div style={{ flex:'2 1 260px', minWidth:'220px' }}>
                <label style={{ fontSize:'0.7rem', fontWeight:600, color:'#475569' }}>文件</label>
                <div style={{ marginTop:'4px', display:'flex', alignItems:'center', gap:'0.6rem' }}>
                  <button disabled={!previewCategory} onClick={()=> fileInputRef.current?.click()} style={{ padding:'6px 12px', borderRadius:'8px', border:'1px solid #334155', background: previewCategory ? '#1e293b' : '#94a3b8', color:'#fff', fontSize:'0.75rem', cursor: previewCategory ? 'pointer':'not-allowed' }}>选择文件</button>
                  <span style={{ fontSize:'0.65rem', color:'#64748b' }}>{pendingImportFile ? pendingImportFile.name : '未选择'}</span>
                </div>
              </div>
            </div>
            <div style={{ border:'1px solid #e2e8f0', borderRadius:'10px', background:'#f8fafc', padding:'0.65rem 0.75rem', maxHeight:'280px', overflow:'auto' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                <strong style={{ fontSize:'0.75rem' }}>预检结果</strong>
                {previewLoading && <span style={{ fontSize:'0.65rem', color:'#475569' }}>处理中...</span>}
                {!previewLoading && previewError && <span style={{ fontSize:'0.65rem', color:'#dc2626' }}>{previewError}</span>}
              </div>
              {!previewLoading && !previewError && previewData && previewData.entries?.length > 0 ? (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
                  <thead>
                    <tr style={{ background:'#e2e8f0' }}>
                      <th style={{ padding:'4px 6px', textAlign:'left' }}>姓名</th>
                      <th style={{ padding:'4px 6px', textAlign:'left' }}>身份证号</th>
                      <th style={{ padding:'4px 6px', textAlign:'left' }}>部门</th>
                      <th style={{ padding:'4px 6px', textAlign:'left' }}>岗位</th>
                      <th style={{ padding:'4px 6px', textAlign:'left' }}>动作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.entries.slice(0,200).map((e,i)=>(
                      <tr key={i} style={{ background: i%2? '#f1f5f9':'#fff' }}>
                        <td style={{ padding:'3px 6px' }}>{e.name}</td>
                        <td style={{ padding:'3px 6px', fontFamily:'monospace' }}>{e.id_card_number}</td>
                        <td style={{ padding:'3px 6px' }}>{e.department}</td>
                        <td style={{ padding:'3px 6px' }}>{e.position}</td>
                        <td style={{ padding:'3px 6px', color: e.action==='insert' ? '#16a34a' : e.action==='update' ? '#2563eb' : '#64748b' }}>{e.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (!previewLoading && !previewError && previewCategory && pendingImportFile && <div style={{ fontSize:'0.65rem', color:'#64748b' }}>没有可显示的条目。</div>)}
              {!previewCategory && <div style={{ fontSize:'0.65rem', color:'#64748b' }}>请选择分类后再选择文件进行预检。</div>}
              {previewData && previewData.entries && previewData.entries.length>200 && (
                <div style={{ fontSize:'0.6rem', marginTop:'4px', color:'#475569' }}>仅显示前200条，剩余 {previewData.entries.length-200} 条已省略。</div>
              )}
            </div>
            <div style={{ display:'flex', gap:'0.6rem', justifyContent:'flex-end', marginTop:'4px' }}>
              <button onClick={()=>{ setShowImportModal(false); setPreviewData(null); setPendingImportFile(null); setPreviewError(null); setPreviewCategory(null); }} style={{ padding:'6px 14px', borderRadius:'8px', background:'#f1f5f9', border:'1px solid #cbd5e1', fontSize:'0.75rem', cursor:'pointer' }}>取消</button>
              <button disabled={!previewData || !!previewError || previewLoading || !pendingImportFile} onClick={async ()=>{
                if(!previewCategory){ showError('未选择分类'); return; }
                if(!pendingImportFile){ showError('缺少文件'); return; }
                setImportLoading(true);
                try {
                  const fd = new FormData(); fd.append('file', pendingImportFile);
                  const res = await axios.post(`/employees/import?category=${previewCategory}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
                  setImportResult(res.data); showSuccess(`导入完成: 新增 ${res.data.success||0}, 更新 ${res.data.updated||0}`);
                  setShowImportModal(false); setPreviewData(null); setPendingImportFile(null); setPreviewCategory(null); fetchEmployees();
                } catch(err){ showError(err?.message || '导入失败'); }
                setImportLoading(false);
              }} style={{ padding:'6px 14px', borderRadius:'8px', background:'var(--brand-primary)', color:'#fff', border:'1px solid var(--brand-primary)', fontSize:'0.75rem', cursor:'pointer' }}>{importLoading? '导入中...' : '确认导入'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 导入预检弹窗（含加载与错误状态） */}
      {isPreviewModalOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1400 }}>
          <div style={{ width:'560px', maxWidth:'92%', background:'#fff', borderRadius:'14px', padding:'1.1rem 1.2rem 1.25rem', boxShadow:'0 22px 50px -14px rgba(0,0,0,0.45)' }}>
            <h3 style={{ margin:'0 0 0.75rem', fontSize:'1.05rem' }}>导入预检结果</h3>
            <div style={{ fontSize:'0.8rem', lineHeight:1.6, maxHeight:'320px', overflow:'auto', background:'#f8fafc', padding:'0.7rem 0.8rem', borderRadius:'8px', border:'1px solid #e2e8f0', minHeight:'120px', display:'flex', alignItems: previewLoading ? 'center' : 'flex-start', justifyContent:'center' }}>
              {previewLoading && <span style={{ fontSize:'0.8rem', color:'#475569' }}>预检处理中...</span>}
              {!previewLoading && previewError && <div style={{ color:'#dc2626', fontSize:'0.75rem' }}>{previewError}</div>}
              {!previewLoading && !previewError && previewData && (
                <pre style={{ margin:0, whiteSpace:'pre-wrap', wordBreak:'break-word', width:'100%' }}>{JSON.stringify(previewData,null,2)}</pre>
              )}
              {!previewLoading && !previewError && !previewData && <span style={{ fontSize:'0.75rem', color:'#64748b' }}>无数据</span>}
            </div>
            <p style={{ fontSize:'0.7rem', color:'#64748b', margin:'0.6rem 0 0.9rem' }}>确认后将写入分类 <b>{previewCategory || category}</b> 表；如需取消请点击关闭。</p>
            <div style={{ display:'flex', justifyContent:'flex-end', gap:'0.6rem' }}>
              <button onClick={()=>{ setIsPreviewModalOpen(false); setPreviewData(null); setPendingImportFile(null); setPreviewError(null); setPreviewCategory(null); }} style={{ padding:'0.55rem 0.95rem', background:'#f1f5f9', border:'1px solid #cbd5e1', borderRadius:'8px', fontSize:'0.75rem', cursor:'pointer' }}>关闭</button>
              <button disabled={previewLoading || !!previewError || !previewData || importLoading} onClick={async ()=>{
                if(!pendingImportFile){ showError('文件丢失，请重新选择'); return; }
                if(!previewCategory){ showError('预检分类丢失，请重新预检'); return; }
                setImportLoading(true);
                try {
                  const fd = new FormData(); fd.append('file', pendingImportFile);
                  const res = await axios.post(`/employees/import?category=${previewCategory}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
                  setImportResult(res.data); showSuccess(`导入完成 (分类: ${previewCategory})`); fetchEmployees();
                  setIsPreviewModalOpen(false); setPreviewData(null); setPendingImportFile(null); setPreviewError(null); setPreviewCategory(null);
                } catch(err){ showError(err?.message || '导入失败'); }
                setImportLoading(false);
              }} style={{ padding:'0.55rem 0.95rem', background:'var(--brand-primary)', color:'#fff', border:'1px solid var(--brand-primary)', borderRadius:'8px', fontSize:'0.75rem', cursor:'pointer' }}>{importLoading? '导入中...' : '确认导入'}</button>
            </div>
          </div>
        </div>
      )}

      <EmployeeDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedEmployeeCategory(category);
        }}
        employeeId={selectedEmployeeId}
        canView={canViewEmployeeDetails}
        canEdit={canEditEmployee}
        category={selectedEmployeeCategory || category}
        onUpdate={() => {
          fetchEmployees();
          setIsDetailModalOpen(false);
          setSelectedEmployeeCategory(category);
        }}
      />
    </div>
  );
};

export default EmployeeList;